import React, { useState } from "react";
import SearchIcon from "mdi-react/MagnifyIcon";
import { useAsyncDebounce } from "react-table";
import moment from "moment";
import classes from "../commonGrid.module.css";
import Button from "react-bootstrap/Button";
import FilterPopOver from "./FilterPopOver";
import ColumnHidePopOver from "./ColumnHidePopOver";
import SearchSection from "./SearchSection";
import DownloadExcel from "./DownloadExcel";
import FilterBadge from "./FilterBadge";
import FormBox from "../../FormBox/FormBox";
import { useGrid } from "../../../context/GridContext";
import formatNumber from "format-number";
import { Legend } from "../../../Utils/CustomComponent";

const TableHeader = () => {
  const {
    title,
    preGlobalFilteredRows,
    setDataToPopulate,
    headerGroups,
    rows,
    filterOption,
    allColumns,
    hideColumn,
    gridColumns = null,
    columnData = null,
    viewAllModal,
    totalSection,
    totalSectionArr,
    bulkUpdate,
    switchBtn,
    setSwitchBtn,
    otherKeys,
    searchValue,
    setSearchValue,
    defaultSearch,
    showLegends,
  } = useGrid();
  const [searchActive, setSearchActive] = useState(!!defaultSearch || false);

  const output = switchBtn ? Object?.values(switchBtn) : [];

  const onChange = useAsyncDebounce((value) => {
    if (!value) return setDataToPopulate(preGlobalFilteredRows);

    const lowercaseQuery = value.toLowerCase();
    const rowMatches = (row) =>
      Object.values(row).some((rowValue) => {
        if (typeof rowValue === "number") {
          // Convert number to string and check if it includes the query
          return (
            rowValue.toString().includes(lowercaseQuery) ||
            formatNumber({ prefix: "$", round: 2 })(rowValue.toFixed(5))
              .toString()
              .includes(lowercaseQuery)
          );
        } else if (typeof rowValue === "string") {
          return rowValue.toLowerCase().includes(lowercaseQuery);
        } else if (
          rowValue &&
          typeof rowValue === "object" &&
          rowValue.props &&
          typeof rowValue.props.children === "string"
        ) {
          return rowValue.props.children.toLowerCase().includes(lowercaseQuery);
        }
        return false;
      }) || row?.subRows?.some(rowMatches);
    if (
      preGlobalFilteredRows.filter((innerRow) => innerRow?.subRows?.length)
        ?.length
    ) {
      const filteredSubRows = preGlobalFilteredRows.flatMap(
        (data) => data?.subRows?.filter(rowMatches) || []
      );
      setDataToPopulate(filteredSubRows);
    } else {
      setDataToPopulate(preGlobalFilteredRows.filter(rowMatches) || undefined);
    }
  }, 200);
  const handleClose = () => {
    setSearchActive(false);
    setSearchValue("");
    onChange("");
    setDataToPopulate(preGlobalFilteredRows);
  };

  // Apply Button
  // whwenever you change something do the same in loanlinq/src/hooks/useCommonGrid.js
  const handleApply = (switchBtnState) => {
    let arr = [...preGlobalFilteredRows];

    const updateSwitchBtnApply = (index) =>
      setSwitchBtn((current) => ({
        ...current,
        [index]: { ...current[index], apply: true },
      }));

    filterOption?.forEach((filter, index) => {
      const { type, key } = filter;
      const value = switchBtnState?.[index]?.value;

      if (value === "") {
        updateSwitchBtnApply(index);
        return;
      }

      switch (type) {
        case "date":
          arr = arr.filter(
            (item) =>
              moment(item[key], "MM-DD-YYYY").format("YYYY-MM-DD") === value
          );
          break;
        case "month":
          const monthsAgo = new Date();
          monthsAgo.setMonth(monthsAgo.getMonth() - value);
          arr = arr.filter(
            (item) =>
              new Date(moment(item[key], "MM-DD-YYYY").format("YYYY-MM-DD")) >=
              monthsAgo
          );
          break;
        case "range":
          arr = arr.filter(
            (item) => value.min <= item[key] && item[key] <= value.max
          );
          break;
        case "select":
          if (value !== "all") {
            arr = arr.filter((item) => item[key] === value);
          }
          break;
        default:
          break;
      }

      updateSwitchBtnApply(index);
    });

    setDataToPopulate(arr);
  };
  // whwenever you change something do the same in loanlinq/src/hooks/useCommonGrid.js
  const handleCrossBtn = async (index) => {
    setSwitchBtn((data) => ({
      ...data,
      [index]: { ...otherKeys[index] },
    }));

    handleApply({
      ...switchBtn,
      [index]: { ...otherKeys[index] },
    });
  };
  return (
    <>
      <div
        className={`d-flex justify-content-between align-items-center ${classes.paddingAndHeight}`}
      >
        {searchActive ? (
          <>
            <SearchSection
              classes={classes}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              onChange={onChange}
              handleClose={handleClose}
            />
          </>
        ) : (
          <>
            <h2 className={classes.h2}>{title}</h2>
            {showLegends && <Legend />}
            {bulkUpdate && (
              <div className="d-flex justify-content-center align-items-center">
                <FormBox
                  options={bulkUpdate.options}
                  register={bulkUpdate.register}
                  watch={bulkUpdate.watch}
                  setValue={bulkUpdate.setValue}
                />
                {bulkUpdate.comp}
              </div>
            )}
            <div
              className="d-flex justify-content-between align-items-center pointer"
              role="button"
            >
              <Button variant="secondary">
                <SearchIcon onClick={() => setSearchActive(true)} />
              </Button>
              <DownloadExcel
                title={title}
                headerGroups={headerGroups}
                rows={rows}
                gridColumns={gridColumns}
                columnData={columnData}
                viewAllModal={viewAllModal}
                filterOption={filterOption}
                totalSection={totalSection}
                totalSectionArr={totalSectionArr}
              />
              {filterOption && (
                <FilterPopOver
                  preGlobalFilteredRows={preGlobalFilteredRows}
                  setDataToPopulate={setDataToPopulate}
                  filterOption={filterOption}
                  switchBtn={switchBtn}
                  setSwitchBtn={setSwitchBtn}
                  otherKeys={otherKeys}
                  handleApply={handleApply}
                />
              )}
              {hideColumn && <ColumnHidePopOver allColumns={allColumns} />}
            </div>
          </>
        )}
      </div>
      {output?.length ? (
        <FilterBadge output={output} handleClose={handleCrossBtn} />
      ) : null}
    </>
  );
};

export default TableHeader;
